/**
 * ArStatementsTab — Estado de cuenta por cliente con Autocomplete.
 */
import { useState } from 'react'
import { Autocomplete } from '@/components/Autocomplete'
import type { AutocompleteItem } from '@/components/Autocomplete'
import { useCustomers } from '@/modules/sales/hooks/useSales'
import { useCustomerStatement } from '../hooks/useFinance'
import { useTenantLocale, formatNumber, formatDate } from '@/lib/locale'
import { inputCls } from '@/lib/formStyles'
import { FileText } from 'lucide-react'
import { DataTable } from '@/components/ds/DataTable'
import { EmptyState } from '@/components/EmptyState'
import { usePermission } from '@/hooks/usePermission'

export default function ArStatementsTab() {
  const [customerId, setCustomerId] = useState('')
  const [displayText, setDisplayText] = useState('')
  const locale = useTenantLocale()
  const { isViewOwn } = usePermission()
  const viewOwn = isViewOwn('receivables.statements')

  const { data: customersData } = useCustomers({
    active: 'true',
    page_size: 9999,
    ...(viewOwn ? { seller_own: 'true' } : {}),
  })
  const { data, isLoading } = useCustomerStatement(customerId)

  const customers = customersData?.customers || []
  const customerItems: AutocompleteItem[] = customers.map(c => ({
    id: c.id,
    label: c.legal_name,
    sublabel: c.tax_id,
  }))

  const fmt = (value: string) => {
    const n = parseFloat(value)
    if (n === 0) return ''
    const decimals = n % 1 !== 0 ? 2 : 0
    return `$${formatNumber(Math.abs(n), locale, decimals)}`
  }

  const TYPE_LABEL: Record<string, string> = {
    invoice: 'Factura',
    credit_note: 'Nota de Crédito',
    payment: 'Pago',
  }
  const TYPE_CLS: Record<string, string> = {
    invoice: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300',
    credit_note: 'bg-warning-surface-strong text-warning-content',
    payment: 'bg-success-surface-strong text-success-content',
  }

  return (
    <>
      <div className="mb-5">
        <label htmlFor="arstatementstab-seleccionar-cliente" className="block text-sm font-medium text-content mb-1">Seleccionar cliente</label>
        <div className="max-w-md">
          <Autocomplete id="arstatementstab-seleccionar-cliente"
            items={customerItems}
            value={customerId}
            displayValue={displayText}
            onChange={(val) => {
              // onChange recibe texto cuando el usuario escribe, o '' al limpiar
              if (!val) { setCustomerId(''); setDisplayText('') }
            }}
            onSelect={(item) => {
              setCustomerId(item.id)
              setDisplayText(`${item.label} — ${item.sublabel || ''}`)
            }}
            placeholder="Buscar por nombre o RUT..."
            className={inputCls}
          />
        </div>
      </div>

      {isLoading && <p className="text-center text-sm text-content-subtle py-8">Cargando estado de cuenta...</p>}

      {!customerId && !isLoading && (
        <div className="flex flex-col items-center justify-center py-12 text-content-subtle">
          <FileText size={40} className="mb-3 text-content-subtle" />
          <p className="text-sm">Seleccione un cliente para ver su estado de cuenta.</p>
        </div>
      )}

      {data && customerId && (
        <>
          <div className="flex flex-wrap gap-4 mb-4 text-sm">
            <span className="text-content-muted">Período: {formatDate(data.date_from, locale)} — {formatDate(data.date_to, locale)}</span>
            <span className="font-medium">
              Saldo: <span className={`${parseFloat(data.closing_balance) > 0 ? 'text-danger-content-soft' : 'text-success-content-soft'} font-bold`}>
                ${formatNumber(Math.abs(parseFloat(data.closing_balance)), locale, 0)}
              </span>
            </span>
          </div>

          <DataTable
            columns={[
              { key: 'fecha', header: 'Fecha', render: e => formatDate(e.date, locale) },
              {
                key: 'tipo', header: 'Tipo',
                render: e => (
                  <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${TYPE_CLS[e.type] ?? TYPE_CLS.payment}`}>
                    {TYPE_LABEL[e.type] ?? e.type}
                  </span>
                ),
              },
              { key: 'descripcion', header: 'Descripción', render: e => e.description },
              { key: 'debito', header: 'Débito', numeric: true, render: e => fmt(e.debit) },
              {
                key: 'credito', header: 'Crédito', numeric: true,
                render: e => <span className="text-success-content-soft">{fmt(e.credit)}</span>,
              },
              {
                key: 'saldo', header: 'Saldo', numeric: true,
                render: e => `$${formatNumber(parseFloat(e.balance), locale, 0)}`,
              },
            ]}
            rows={data.entries}
            rowKey={(_, i) => i}
            footer={{
              debito: fmt(data.total_debit),
              credito: <span className="text-success-content-soft">{fmt(data.total_credit)}</span>,
              saldo: `$${formatNumber(parseFloat(data.closing_balance), locale, 0)}`,
            }}
            empty={<EmptyState icon={FileText} size="sm" title="Sin movimientos en el período." />}
            minWidth={760}
            caption="Estado de cuenta del cliente"
          />
        </>
      )}
    </>
  )
}
